/* eslint-disable @typescript-eslint/no-explicit-any */
import { events, logger, security } from '@beelday/common'
import { Config } from 'common/config'
import { RootState } from 'common/redux'
import {
	EventsSourcePath,
	InteractionSchemeAddress,
	QuizQuestionAnswered,
	QuizQuestionAsked,
	QuizQuestionCompleted,
	RoomAddress,
	RoomType,
	UserId,
	WorkflowId,
} from 'common/types'
import BeeldayClient, {
	EtherPadClosedEvent,
	EtherPadOpenedEvent,
	ExcalidrawClosedEvent,
	ExcalidrawGroupSwitchedEvent,
	ExcalidrawOpenedEvent,
	GroupPropertiesChangedEvent,
	GroupsInitialStateEvents,
	GroupStateChangedEvent,
	HandPositionChangedEvent,
	PlayersChangedEvent,
	QuizConcludedEvent,
	RoomStateChangedEvent,
	TrainerBecameAvailableEvent,
	TrainerBecameUnavailableEvent,
	TrainerIsJoiningGroupEvent,
	TrainerStatusChangedEvent,
} from 'connectivity/beelday-client'
import { useAuthenticatedBeeldayClient } from 'connectivity/beelday-hooks'
import { toGroupSuggestions } from 'connectivity/model-mappers'
import { setISRoomTransferWithCountdown } from 'interaction-scheme/redux'
import {
	FunctionComponent,
	MutableRefObject,
	useCallback,
	useEffect,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Dispatch } from 'redux'
import { setRoomUsers, setTrainingRoomPhase } from 'room/common/redux'
import {
	changeGroupName,
	handleEtherPadClosed,
	handleEtherPadOpened,
	handleExcalidrawClosed,
	handleExcalidrawOpened,
	handleGroupStateChangedEvent,
	handleHandPositionChangedEvent,
	handleTrainerBecameAvailableEvent,
	handleTrainerBecameUnavailableEvent,
	handleTrainerIsJoiningGroupEvent,
	handleTrainerStatusChangedEvent,
} from 'room/group-room/redux'
import { handlePublicChatRoomChanged } from 'room/lobby/redux'
import {
	handlePlayersChanged,
	handleQuestionAnswered,
	handleQuestionAsked,
	handleQuestionCompleted,
	handleQuizConcluded,
	handleReviewEtherPadClosed,
	handleReviewEtherPadGroupSwitched,
	handleReviewEtherPadOpened,
	handleReviewExcalidrawClosed,
	handleReviewExcalidrawGroupSwitched,
	handleReviewExcalidrawOpened,
	setGroupPartitionMap,
	setGroupsSuggestionAssignment,
	setReviewActiveGroup,
} from 'room/training-room/redux'
import { GroupsSuggestionAssignment } from 'room/training-room/set-groups/model/groups-suggestion-assignment'
import { CORRUPT_STREAM_EVENT } from 'utils/event-source'
import { assertUnreachable } from 'utils/unreachable'
import {
	setLatestInteractionSchemeEvent,
	setLatestWorkflowEvent,
	setStreamStatus,
} from './redux'
import { StreamStatus } from './stream-supervisor'
import { useFreshLogin } from './use-fresh-login'
import { useStreamSupervisor } from './use-stream-supervisor'

function getWorkflowEventSource(
	workflowId: WorkflowId,
	dispatch: Dispatch<any>,
	beeldayClient: BeeldayClient
) {
	return beeldayClient.workflowEventSource({
		workflowId: workflowId,
		onWorkflowEvent: event => dispatch(setLatestWorkflowEvent(event)),
	})
}

function getInteractionSchemeEventSource(
	address: InteractionSchemeAddress,
	dispatch: Dispatch<any>,
	beeldayClient: BeeldayClient,
	userId: UserId
) {
	return beeldayClient.interactionSchemeEventSource({
		workflowId: address.workflowId,
		interactionSchemeId: address.interactionSchemeId,
		onInitialStateEvents: event =>
			event.initialEvents.forEach(e => dispatch(events.toSSEAction(e))),
		onInteractionSchemeStateChange: event =>
			dispatch(setLatestInteractionSchemeEvent(event)),
		onInteractionSchemeRoomTransferWithCountdown: event =>
			dispatch(setISRoomTransferWithCountdown(event, userId)),
		onWorkflowEvent: event => dispatch(setLatestWorkflowEvent(event)),
	})
}

function getTrainingRoomEventSource(
	address: RoomAddress,
	dispatch: Dispatch<any>,
	beeldayClient: BeeldayClient,
	userId: UserId
) {
	return beeldayClient.trainingRoomEventSource({
		workflowId: address.workflowId,
		interactionSchemeId: address.interactionSchemeId,
		roomId: address.roomId,
		roomType: address.roomType,
		onInteractionSchemeStateChange: event =>
			dispatch(setLatestInteractionSchemeEvent(event)),
		onInteractionSchemeRoomTransferWithCountdown: event =>
			dispatch(setISRoomTransferWithCountdown(event, userId)),
		onWorkflowEvent: event => dispatch(setLatestWorkflowEvent(event)),
		onInitialStateEvent: event =>
			event.initialEvents.forEach(e => {
				switch (e.eventType) {
					case 'ACTIVE_GROUP_CHANGED':
						dispatch(setReviewActiveGroup(e.id))
						break
					case 'ETHER_PAD_OPENED':
						dispatch(handleReviewEtherPadOpened(e.groupId))
						break
					case 'EXCALIDRAW_OPENED':
						dispatch(handleReviewExcalidrawOpened(e.groupId))
						break
					case 'QUESTION_ASKED':
						dispatch(handleQuestionAsked(e))
						break
					case 'QUESTION_ANSWERED':
						dispatch(handleQuestionAnswered(e))
						break
					case 'QUESTION_COMPLETED':
						dispatch(handleQuestionCompleted(e))
						break
					case 'QUIZ_CONCLUDED':
						dispatch(handleQuizConcluded(e))
						break
					case 'ROOM_PHASE_CHANGED':
						dispatch(setTrainingRoomPhase(e.phase))
						break
					case 'GROUPS_SUGGESTION_STATE_CHANGED':
						dispatch(
							setGroupsSuggestionAssignment(
								new GroupsSuggestionAssignment(e.groups.map(toGroupSuggestions))
							)
						)
						break
					case 'GROUPS_PARTITION_MAP':
						dispatch(
							setGroupPartitionMap(
								e.partitionMap.sort((a, b) => a.maxGroupSize - b.maxGroupSize)
							)
						)
						break
					default:
						dispatch(events.toSSEAction(e))
				}
			}),
		onRoomStateChangedEvent: event => dispatch(setRoomUsers(event.state.users)),
		onGroupSuggestionStateChangedEvent: event =>
			dispatch(
				setGroupsSuggestionAssignment(
					new GroupsSuggestionAssignment(event.groups.map(toGroupSuggestions))
				)
			),
		onTrainingRoomGroupPartitionEvent: event =>
			dispatch(
				setGroupPartitionMap(
					event.partitionMap.sort((a, b) => a.maxGroupSize - b.maxGroupSize)
				)
			),
		onTrainingRoomPhaseChangedEvent: event =>
			dispatch(setTrainingRoomPhase(event.phase)),
		onReviewActiveGroupChangedEvent: event =>
			dispatch(setReviewActiveGroup(event.id)),
		onEtherPadOpened(event: EtherPadOpenedEvent) {
			dispatch(handleReviewEtherPadOpened(event.groupId))
		},
		onEtherPadClosed(event: EtherPadClosedEvent) {
			dispatch(handleReviewEtherPadClosed(event.groupId))
		},
		onEtherPadGroupSwitched(event) {
			dispatch(
				handleReviewEtherPadGroupSwitched(event.groupId, event.previousGroup)
			)
		},
		onExcalidrawOpened(event: ExcalidrawOpenedEvent) {
			dispatch(handleReviewExcalidrawOpened(event.groupId))
		},
		onExcalidrawClosed(event: ExcalidrawClosedEvent) {
			dispatch(handleReviewExcalidrawClosed(event.groupId))
		},
		onExcalidrawGroupSwitched(event: ExcalidrawGroupSwitchedEvent) {
			dispatch(
				handleReviewExcalidrawGroupSwitched(event.groupId, event.previousGroup)
			)
		},
		onQuestionAsked(event: QuizQuestionAsked) {
			dispatch(handleQuestionAsked(event))
		},
		onQuestionAnswered(event: QuizQuestionAnswered) {
			dispatch(handleQuestionAnswered(event))
		},
		onQuestionCompleted(event: QuizQuestionCompleted) {
			dispatch(handleQuestionCompleted(event))
		},
		onQuizConcluded(event: QuizConcludedEvent) {
			dispatch(handleQuizConcluded(event))
		},
		onPlayersChanged(event: PlayersChangedEvent) {
			dispatch(handlePlayersChanged(event))
		},
		onEvent(event: unknown) {
			dispatch(events.toSSEAction(event))
		},
	})
}

function getGroupRoomEventSource(
	address: RoomAddress,
	dispatch: Dispatch<any>,
	beeldayClient: BeeldayClient,
	userId: UserId
) {
	return beeldayClient.groupRoomEventSource({
		workflowId: address.workflowId,
		interactionSchemeId: address.interactionSchemeId,
		roomId: address.roomId,
		roomType: address.roomType,
		onEvent(event: any) {
			dispatch(events.toSSEAction(event))
		},
		onInteractionInitialState: event => {
			event.initialEvents.forEach(e => dispatch(events.toSSEAction(e)))
		},
		onInteractionSchemeStateChange: event =>
			dispatch(setLatestInteractionSchemeEvent(event)),
		onInteractionSchemeRoomTransferWithCountdown: event =>
			dispatch(setISRoomTransferWithCountdown(event, userId)),
		onWorkflowEvent: event => dispatch(setLatestWorkflowEvent(event)),
		onHandPositionChangedEvent(event: HandPositionChangedEvent) {
			dispatch(handleHandPositionChangedEvent(event))
		},
		onGroupStateChangedEvent(event: GroupStateChangedEvent) {
			dispatch(handleGroupStateChangedEvent(event))
		},
		onTrainerIsJoiningGroupEvent(event: TrainerIsJoiningGroupEvent) {
			dispatch(handleTrainerIsJoiningGroupEvent(event))
		},
		onTrainerStatusChangedEvent(event: TrainerStatusChangedEvent) {
			dispatch(handleTrainerStatusChangedEvent(event))
		},
		onTrainerBecameUnavailableEvent(event: TrainerBecameUnavailableEvent) {
			dispatch(handleTrainerBecameUnavailableEvent(event))
		},
		onRoomStateChangedEvent(event: RoomStateChangedEvent) {
			dispatch(setRoomUsers(event.state.users))
		},
		onTrainerBecameAvailableEvent(event: TrainerBecameAvailableEvent) {
			dispatch(handleTrainerBecameAvailableEvent(event))
		},
		onGroupPropertiesChanged(event: GroupPropertiesChangedEvent) {
			dispatch(changeGroupName(event.id, event.name))
		},
		onEtherPadOpened(event: EtherPadOpenedEvent) {
			dispatch(handleEtherPadOpened(event.groupId))
		},
		onEtherPadClosed(event: EtherPadClosedEvent) {
			dispatch(handleEtherPadClosed(event))
		},
		onExcalidrawOpened(event: ExcalidrawOpenedEvent) {
			dispatch(handleExcalidrawOpened(event.groupId))
		},
		onExcalidrawClosed(event: ExcalidrawClosedEvent) {
			dispatch(handleExcalidrawClosed(event.groupId))
		},
		onGroupsInitialStateEvent(event: GroupsInitialStateEvents) {
			event.initialEvents.forEach(e => {
				switch (e.eventType) {
					case 'HAND_POSITION_CHANGED':
						dispatch(handleHandPositionChangedEvent(e))
						break
					case 'TRAINER_STATUS_CHANGED':
						dispatch(handleTrainerStatusChangedEvent(e))
						break
					case 'ETHER_PAD_OPENED':
						dispatch(handleEtherPadOpened(e.groupId))
						break
					case 'EXCALIDRAW_OPENED':
						dispatch(handleExcalidrawOpened(e.groupId))
						break
					case 'GROUPS_STATE_CHANGED':
						dispatch(handleGroupStateChangedEvent(e))
						break
					case 'TRAINER_BECAME_UNAVAILABLE':
						dispatch(handleTrainerBecameUnavailableEvent(e))
						break
					default:
						dispatch(events.toSSEAction(e))
				}
			})
		},
	})
}

function getLobbyEventSource(
	address: RoomAddress,
	dispatch: Dispatch<any>,
	beeldayClient: BeeldayClient,
	userId: UserId
) {
	return beeldayClient.lobbyEventSource({
		workflowId: address.workflowId,
		interactionSchemeId: address.interactionSchemeId,
		roomId: address.roomId,
		roomType: address.roomType,
		onInteractionSchemeStateChange: event =>
			dispatch(setLatestInteractionSchemeEvent(event)),
		onInteractionSchemeRoomTransferWithCountdown: event =>
			dispatch(setISRoomTransferWithCountdown(event, userId)),
		onWorkflowEvent: event => dispatch(setLatestWorkflowEvent(event)),
		onInitialStateEvent: event =>
			event.initialEvents.forEach(e => dispatch(events.toSSEAction(e))),
		onRoomStateChangedEvent: event => {
			//HERE COMES CHANGES FROM LOBBY & PUBLIC CHAT ROOMS
			if (event.id === address.roomId) {
				dispatch(setRoomUsers(event.state.users))
			} else {
				dispatch(handlePublicChatRoomChanged(event.id, event.state.users))
			}
		},
		onEvent(event: any) {
			dispatch(events.toSSEAction(event))
		},
	})
}

function getPublicChatEventSource(
	address: RoomAddress,
	dispatch: Dispatch<any>,
	beeldayClient: BeeldayClient,
	userId: UserId
) {
	return beeldayClient.publicChatEventSource({
		workflowId: address.workflowId,
		interactionSchemeId: address.interactionSchemeId,
		roomId: address.roomId,
		roomType: address.roomType,
		onInteractionSchemeStateChange: event =>
			dispatch(setLatestInteractionSchemeEvent(event)),
		onInteractionSchemeRoomTransferWithCountdown: event =>
			dispatch(setISRoomTransferWithCountdown(event, userId)),
		onWorkflowEvent: event => dispatch(setLatestWorkflowEvent(event)),
		onRoomStateChangedEvent: event => {
			//HERE COME CHANGES FROM CURRENT ROOM & OTHER PUBLIC CHAT ROOMS
			if (event.id === address.roomId) {
				dispatch(setRoomUsers(event.state.users))
			}

			dispatch(handlePublicChatRoomChanged(event.id, event.state.users))
		},
		onEvent(event) {
			dispatch(events.toSSEAction(event))
		},
		onInitialStateEvent(event) {
			event.initialEvents.forEach(e => dispatch(events.toSSEAction(e)))
		},
	})
}

function getRoomEventSource(
	address: RoomAddress,
	dispatch: Dispatch<any>,
	roomType: RoomType,
	beeldayClient: BeeldayClient,
	userId: UserId
) {
	switch (roomType) {
		case RoomType.TrainingRoom:
			return getTrainingRoomEventSource(
				address,
				dispatch,
				beeldayClient,
				userId
			)
		case RoomType.WebinarRoom:
			return getTrainingRoomEventSource(
				address,
				dispatch,
				beeldayClient,
				userId
			)
		case RoomType.GroupRoom:
			return getGroupRoomEventSource(address, dispatch, beeldayClient, userId)
		case RoomType.Lobby:
			return getLobbyEventSource(address, dispatch, beeldayClient, userId)
		case RoomType.PublicChat:
			return getPublicChatEventSource(address, dispatch, beeldayClient, userId)
		default:
			assertUnreachable('RoomType', roomType)
	}
}

/**
 * On the workflow's own stream the room has yet to be joined: if it has not
 * been within this long, the workflow is joined again and the stream
 * reopened, until the room moves on.
 */
const WORKFLOW_REJOIN_MS = 7000

export const EventSourceManager: FunctionComponent = () => {
	const eventsSourcePath = useSelector(
		(state: RootState) => state.eventSource.eventsSourcePath
	)
	const workflow = useSelector((state: RootState) => state.workflow)

	const interactionScheme = useSelector(
		(state: RootState) => state.interactionScheme
	)

	const dispatch = useDispatch()
	const beeldayClient = useAuthenticatedBeeldayClient()
	const user = security.useAuthenticatedUser()

	/** The stream for where the room is now; nothing while there is nowhere to connect. */
	const openStream = useCallback((): EventSource | undefined => {
		logger.debug('START CONNECTING TO EVENT SOURCE')
		switch (eventsSourcePath) {
			case EventsSourcePath.room: {
				if (
					!workflow.workflowId ||
					!workflow.joinedInteractionScheme ||
					!interactionScheme.joinedRoom ||
					!user
				) {
					return undefined
				}
				const address: RoomAddress = {
					workflowId: workflow.workflowId,
					...workflow.joinedInteractionScheme,
					...interactionScheme.joinedRoom,
				}

				logger.debug('CONNECTING TO EVENT SOURCE ROOM')
				return getRoomEventSource(
					address,
					dispatch,
					interactionScheme.joinedRoom.roomType,
					beeldayClient,
					user.id
				)
			}
			case EventsSourcePath.interactionScheme: {
				if (
					!workflow.workflowId ||
					!workflow.joinedInteractionScheme ||
					!user
				) {
					return undefined
				}
				const address: InteractionSchemeAddress = {
					workflowId: workflow.workflowId,
					...workflow.joinedInteractionScheme,
				}

				logger.debug('CONNECTING TO EVENT SOURCE INTERACTION SCHEME')
				return getInteractionSchemeEventSource(
					address,
					dispatch,
					beeldayClient,
					user.id
				)
			}
			case EventsSourcePath.workflow: {
				if (!workflow.workflowId) {
					return undefined
				}
				logger.debug('CONNECTING TO EVENT SOURCE WORKFLOW')
				return getWorkflowEventSource(
					workflow.workflowId,
					dispatch,
					beeldayClient
				)
			}
		}
		return undefined
	}, [
		beeldayClient,
		dispatch,
		eventsSourcePath,
		interactionScheme.joinedRoom,
		user,
		workflow.joinedInteractionScheme,
		workflow.workflowId,
	])

	const ensureFreshLogin = useFreshLogin()
	const onStatus = useCallback(
		(status: StreamStatus) => dispatch(setStreamStatus(status)),
		[dispatch]
	)
	const { streamRef, reopen } = useStreamSupervisor({
		open: openStream,
		ensureFreshLogin,
		onStatus,
		timeoutMs: Config.eventSourceCloseTimeout,
	})

	useReconnectOnCorruptStream(streamRef, reopen)
	useRejoinStuckWorkflow(
		eventsSourcePath === EventsSourcePath.workflow
			? workflow.workflowId ?? undefined
			: undefined,
		beeldayClient,
		reopen
	)

	return <></>
}

/** `WORKFLOW_REJOIN_MS`, for as long as the room is on the workflow's stream. */
const useRejoinStuckWorkflow = (
	workflowId: WorkflowId | undefined,
	beeldayClient: BeeldayClient,
	reopen: () => void
): void => {
	useEffect(() => {
		if (!workflowId) return
		let active = true
		let timer: ReturnType<typeof setTimeout>
		const rejoinWhenStuck = () => {
			timer = setTimeout(() => {
				logger.debug('TRIGGER EVENT SOURCE TIMEOUT')
				beeldayClient
					.joinWorkflow({ workflowId })
					.then(() => {
						if (active) reopen()
					})
					.catch(error =>
						logger.warn('Could not join the workflow again', error)
					)
					.finally(() => {
						if (active) rejoinWhenStuck()
					})
			}, WORKFLOW_REJOIN_MS)
		}
		rejoinWhenStuck()
		return () => {
			active = false
			clearTimeout(timer)
		}
	}, [beeldayClient, reopen, workflowId])
}

/**
 * A stream that delivered an event it could not parse has lost events, and
 * the room on screen is no longer the server's - a phone stayed on "The
 * training hasn't started yet" for a whole session. Reconnecting gets the
 * initial state again. Through the ref, so the stream a reconnect opens is
 * the one watched, without waiting for a render.
 */
export const useReconnectOnCorruptStream = (
	eventSourceRef: MutableRefObject<EventSource | undefined>,
	reconnect: () => void
): void => {
	useEffect(() => {
		const onCorrupt = (event: Event) => {
			const source = eventSourceRef.current
			if (source && event instanceof CustomEvent && event.detail === source) {
				logger.warn('Reconnecting after an unreadable event')
				reconnect()
			}
		}
		window.addEventListener(CORRUPT_STREAM_EVENT, onCorrupt)
		return () => window.removeEventListener(CORRUPT_STREAM_EVENT, onCorrupt)
	}, [eventSourceRef, reconnect])
}
